import { useState } from 'react';
import { Button } from '@app/components/Button/Button';
import { getPlans, updatePlanAdmin } from '@app/services/plans';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { useForm, FormProvider } from 'react-hook-form';
import { InputField } from '@app/components/Fields/InputField';
import { CheckboxField } from '@app/components/Fields/CheckboxField';
import { Select } from '@app/components/Select/Select';
import { toast } from 'react-toastify';

const PLAN_TYPE_OPTIONS = [
  // { value: 'BASIC', label: 'Basic' },
  // { value: 'PROFESSIONAL', label: 'Professional' },
  // { value: 'ENTERPRISE', label: 'Enterprise' },
  { value: 'FREE', label: 'Free' },
  { value: 'PRO', label: 'Pro' },
  { value: 'GROWTH', label: 'Growth' },
];

const INTERVAL_OPTIONS = [
  { value: 'month', label: 'Monthly' },
  { value: 'year', label: 'Yearly' },
];

const CURRENCY_OPTIONS = [
  { value: 'usd', label: 'USD' },
  { value: 'eur', label: 'EUR' },
  { value: 'gbp', label: 'GBP' },
];

interface Plan {
  id: string;
  name: string;
  planId: string;
  price?: number;
  interval?: string;
  features?: { id: string; name: string }[];
  isActive?: boolean;
  priceId?: string;
  type?: string;
  reviewRequestLimit?: number;
  seatLimit?: number;
  contactUploadLimit?: number;
  businessLimit?: number;
  analyticsDepth?: number;
  aiReplyLimit?: number;
  currency?: string;
  aiEnabled: boolean;
  automationEnabled: boolean;
  smsEnabled: boolean;
  googleSheetsImportEnabled: boolean;
  customerTagsEnabled: boolean;
  brandingDisabled: boolean;
}

export const AdminPlans = () => {
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const queryClient = useQueryClient();

  // Fetch plans using existing service
  const {
    data: plansResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['admin-plans'],
    queryFn: getPlans,
  });
  const plans: Plan[] = plansResponse?.data?.plans || [];

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: any }) =>
      updatePlanAdmin(id, data),
    onSuccess: () => {
      toast.success('Plan updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['admin-plans'] });
      setEditingPlan(null);
    },
    onError: () => {
      toast.error('Failed to update plan. Please try again.');
    },
  });

  const handleUpdatePlan = async (planData: {
    type: string;
    reviewRequestLimit: number;
    seatLimit: number;
    price: number;
    priceId: string;
    currency: string;
    interval: string;
    isActive: boolean;
    features: string[];
    aiEnabled: boolean;
    automationEnabled: boolean;
  }) => {
    if (!editingPlan?.id) {
      return;
    }
    updateMutation.mutate({ id: editingPlan.id, data: planData });
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-gray-500">Loading plans...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-red-500">Error loading plans: {error.message}</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Plans Management</h1>
          <p className="mt-1 text-sm text-gray-500">Manage subscription plans and pricing</p>
        </div>
      </div>

      {/* Plans Grid */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg leading-6 font-medium text-gray-900">Plans ({plans.length})</h3>
          <p className="mt-1 max-w-2xl text-sm text-gray-500">Manage subscription plans and pricing</p>
        </div>

        {plans.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-gray-500">No plans found</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {plans.map((plan, index) => (
              <div key={plan.id || `plan-${index}`} className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow duration-200">
                <div className="flex justify-between items-start mb-4">
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">{plan.type || 'Unknown Plan'}</h4>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium mt-2 ${plan.isActive !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                    >
                      {plan.isActive !== false ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold text-gray-900">{plan.price ? `$${plan.price}` : plan.type == 'FREE' ? '$0' : 'Not set'}</div>
                    {plan.interval && <div className="text-sm text-gray-500">per {plan.interval.toLowerCase()}</div>}
                  </div>
                </div>
                <div className="space-y-3 mb-4">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Review requests:</span>
                    <span className="font-medium">{plan.reviewRequestLimit || 'Unlimited'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Seats:</span>
                    <span className="font-medium">{plan.seatLimit || 'Unlimited'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">CSV upload customers:</span>
                    <span className="font-medium">{plan.contactUploadLimit || 'Unlimited'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Locations:</span>
                    <span className="font-medium">{plan.businessLimit || 'Unlimited'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Analytics history:</span>
                    <span className="font-medium">
                      {plan.analyticsDepth} {(plan.analyticsDepth ?? 0) > 1 ? 'months' : 'month'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">AI review replies:</span>
                    <span className="font-medium">{plan.aiReplyLimit || 'Unlimited'}</span>
                  </div>
                  {/*<div className="flex justify-between text-sm">*/}
                  {/*  <span className="text-gray-500">AI:</span>*/}
                  {/*  <span className={`font-medium ${plan.aiEnabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.aiEnabled ? 'Enabled' : 'Disabled'}</span>*/}
                  {/*</div>*/}
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Automation:</span>
                    <span className={`font-medium ${plan.automationEnabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.automationEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Sms:</span>
                    <span className={`font-medium ${plan.smsEnabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.smsEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Google Sheets import:</span>
                    <span className={`font-medium ${plan.googleSheetsImportEnabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.googleSheetsImportEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Customer tags:</span>
                    <span className={`font-medium ${plan.customerTagsEnabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.customerTagsEnabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-500">Remove Echo branding:</span>
                    <span className={`font-medium ${plan.brandingDisabled ? 'text-green-600' : 'text-gray-400'}`}>{plan.brandingDisabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </div>

                {plan.features && plan.features.length > 0 && (
                  <div className="mb-4">
                    <div className="text-sm font-medium text-gray-700 mb-2">Features:</div>
                    <div className="space-y-1">
                      {plan.features.slice(0, 3).map((feature, featureIndex) => (
                        <div key={featureIndex} className="flex items-center text-xs text-gray-600">
                          <svg className="w-3 h-3 text-green-500 mr-1 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="truncate">{feature.name}</span>
                        </div>
                      ))}
                      {plan.features.length > 3 && <div className="text-xs text-gray-500">+{plan.features.length - 3} more</div>}
                    </div>
                  </div>
                )}
                <div className="pt-4 border-t border-gray-100">
                  <Button onClick={() => setEditingPlan(plan)} kind="primary" size="small" className="w-full">
                    Edit Plan
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Update Plan Modal */}
      {editingPlan && (
        <Dialog open={true} onClose={() => setEditingPlan(null)} className="relative z-50">
          <DialogBackdrop className="fixed inset-0 bg-black/50" />
          <div className="fixed inset-0 flex w-screen items-center justify-center p-4">
            <DialogPanel className="space-y-4 border bg-white w-full max-w-3xl rounded-xl max-h-[90vh] overflow-y-auto">
              <PlanForm plan={editingPlan} onSubmit={handleUpdatePlan} onCancel={() => setEditingPlan(null)} title="Update Plan" updateMutation={updateMutation} />
            </DialogPanel>
          </div>
        </Dialog>
      )}
    </div>
  );
};

interface PlanFormProps {
  plan: Plan;
  onSubmit: (planData: {
    type: string;
    reviewRequestLimit: number;
    seatLimit: number;
    price: number;
    priceId: string;
    currency: string;
    interval: string;
    isActive: boolean;
    features: string[];
    aiEnabled: boolean;
    automationEnabled: boolean;
    aiReplyLimit: number;
    analyticsDepth: number;
    brandingDisabled: boolean;
    businessLimit: number;
    contactUploadLimit: number;
    customerTagsEnabled: boolean;
    googleSheetsImportEnabled: boolean;
    smsEnabled: boolean;
  }) => void;
  onCancel: () => void;
  title: string;
  updateMutation: any;
}

const PlanForm = ({
  plan,
  onSubmit,
  onCancel,
  title,
  updateMutation,
}: PlanFormProps) => {
  const editForm = useForm({
    defaultValues: {
      type: plan.type || 'FREE',
      reviewRequestLimit: plan.reviewRequestLimit === null ? null : plan.reviewRequestLimit || 1,
      reviewRequestUnlimited: plan.reviewRequestLimit === null,
      seatLimit: plan.seatLimit || 1,
      price: plan.price || 0,
      priceId: plan.priceId || plan.planId || '',
      currency: plan.currency || 'usd',
      interval: plan.interval || 'month',
      isActive: plan.isActive !== false,
      features: Array.isArray(plan.features)
        ? plan.features
            .map((f) => {
              if (typeof f === 'string') return f;
              if (typeof f === 'object' && f !== null && 'name' in f) return f.name;
              return JSON.stringify(f);
            })
            .join('\n')
        : '',
      aiEnabled: plan.aiEnabled || false,
      automationEnabled: plan.automationEnabled || false,
      brandingDisabled: plan.brandingDisabled || false,
      customerTagsEnabled: plan.customerTagsEnabled || false,
      googleSheetsImportEnabled: plan.googleSheetsImportEnabled || false,
      smsEnabled: plan.smsEnabled || false,
      analyticsDepth: plan.analyticsDepth || 1,
      businessLimit: plan.businessLimit || 1,
      aiReplyLimit: plan.aiReplyLimit === null ? null : plan.aiReplyLimit,
      aiReplyUnlimited: plan.aiReplyLimit === null,
      contactUploadLimit: plan.contactUploadLimit === null ? null : plan.contactUploadLimit,
      contactUploadUnlimited: plan.contactUploadLimit === null,
    },
  });

  const reviewRequestUnlimited = editForm.watch('reviewRequestUnlimited');
  const contactUploadUnlimited = editForm.watch('contactUploadUnlimited');
  const aiReplyUnlimited = editForm.watch('aiReplyUnlimited');

  const handleSubmit = (values: any) => {

    const planData = {
      ...values,
      features: values.features ? values.features.split('\n').filter((f: string) => f.trim() !== '') : [],
      reviewRequestLimit: values.reviewRequestUnlimited ? null : parseInt(values.reviewRequestLimit) || 0,
      contactUploadLimit: values.contactUploadUnlimited ? null : parseInt(values.contactUploadLimit) || 0,
      aiReplyLimit: values.aiReplyUnlimited ? null : parseInt(values.aiReplyLimit) || 0,
      seatLimit: parseInt(values.seatLimit) || 0,
      price: parseFloat(values.price) || 0,
    };

    onSubmit(planData);
  };

  return (
    <FormProvider {...editForm}>
      <div className="p-5">
        <h3 className="text-lg font-medium text-gray-900 mb-4">{title}</h3>

        <form onSubmit={editForm.handleSubmit(handleSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              name="type"
              label="Plan Type"
              options={PLAN_TYPE_OPTIONS}
              onChange={(value) => editForm.setValue('type', value)}
              value={editForm.watch('type') || ''}
              placeholder="Select plan type"
              containerClass="w-full"
              disabled
            />
            <InputField name="priceId" label="Price ID" type="text" disabled className="cursor-not-allowed opacity-50" />
            <InputField name="price" label="Price" type="number" step="0.01" required />
            <Select
              name="currency"
              label="Currency"
              options={CURRENCY_OPTIONS}
              onChange={(value) => editForm.setValue('currency', value)}
              value={editForm.watch('currency') || ''}
              placeholder="Select currency"
              containerClass="w-full"
            />
            <Select
              name="interval"
              label="Interval"
              options={INTERVAL_OPTIONS}
              onChange={(value) => editForm.setValue('interval', value)}
              value={editForm.watch('interval') || ''}
              placeholder="Select interval"
              containerClass="w-full"
              disabled
            />
            <InputField name="businessLimit" label="Locations Limit" type="number" required />
            <InputField name="seatLimit" label="Seat Limit" type="number" required />
            <InputField name="analyticsDepth" label="Analytics history" type="number" required />
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">Review Request Limit</span>
                <div className="flex items-center space-x-2">
                  <CheckboxField name="reviewRequestUnlimited" />
                  <span className="text-sm font-semibold text-gray-800 cursor-pointer">Unlimited</span>
                </div>
              </div>
              <InputField name="reviewRequestLimit" type="number" containerClass={reviewRequestUnlimited ? 'opacity-50' : ''} required={!reviewRequestUnlimited} disabled={reviewRequestUnlimited} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">CSV upload customers limit</span>
                <div className="flex items-center space-x-2">
                  <CheckboxField name="contactUploadUnlimited" />
                  <span className="text-sm font-semibold text-gray-800 cursor-pointer">Unlimited</span>
                </div>
              </div>
              <InputField name="contactUploadLimit" type="number" containerClass={contactUploadUnlimited ? 'opacity-50' : ''} required={!contactUploadUnlimited} disabled={contactUploadUnlimited} />
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <span className="font-medium text-sm">AI review replies Limit</span>
                <div className="flex items-center space-x-2">
                  <CheckboxField name="aiReplyUnlimited" />
                  <span className="text-sm font-semibold text-gray-800 cursor-pointer">Unlimited</span>
                </div>
              </div>
              <InputField name="aiReplyLimit" type="number" containerClass={aiReplyUnlimited ? 'opacity-50' : ''} required={!aiReplyUnlimited} disabled={aiReplyUnlimited} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Features</label>
            <textarea
              {...editForm.register('features')}
              rows={6}
              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
              placeholder="Enter each feature on a new line&#10;e.g.,&#10;Email Support&#10;Basic Analytics&#10;Up to 2 Team Members"
            />
            <p className="mt-1 text-xs text-gray-500">Enter each feature on a separate line</p>
          </div>

          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <CheckboxField name="isActive" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">Active Plan</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="aiEnabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">AI Enabled</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="automationEnabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">Automation Enabled</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="googleSheetsImportEnabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">Google Sheets import Enabled</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="customerTagsEnabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">Customer tags Enabled</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="brandingDisabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">Remove Echo branding Enabled</label>
            </div>
            <div className="flex items-center space-x-3">
              <CheckboxField name="smsEnabled" />
              <label className="text-sm font-semibold text-gray-800 cursor-pointer">SMS review requests Enabled</label>
            </div>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <Button type="button" onClick={onCancel} kind="secondary" disabled={updateMutation?.isPending || false}>
              Cancel
            </Button>
            <Button type="submit" kind="primary" loading={updateMutation?.isPending || false} disabled={updateMutation?.isPending || false}>
              Update Plan
            </Button>
          </div>
        </form>
      </div>
    </FormProvider>
  );
};
